import { useCallback, useEffect, useState } from "react";
import { Search } from "react-bootstrap-table2-toolkit";
import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Create, Refresh, Add, Delete } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import IconButtonLoader from "components/Buttons/IconButtonLoader";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import ToggleCell from "components/Cells/ToggleCell";
import FormDefectReasonDialog from "./components/form-dialog";
import Api from "lib/api/httpRequestHelper";
import useLogs from "hooks/logs";
import { IsMobile } from "utils/is-mobile";

const getTableColumns = (setSelectedDefect, toggleFormDialog, searching) => [
  {
    dataField: "IdDefeito",
    text: "Cod Motivo",
    sort: true,
    formatter: (defect) => (searching ? <Skeleton variant="text" /> : defect),
  },
  {
    dataField: "Defeito",
    text: "Descrição do Motivo de Defeito de Produto",
    sort: true,
    formatter: (defect) => (searching ? <Skeleton variant="text" /> : defect),
  },
  {
    dataField: "Status",
    text: "Ativo",
    headerStyle: { width: 90 },
    formatter: (cell, row) => {
      return (
        <ToggleCell
          initialValue={row.Status === 1}
          handleChange={() => {
            setSelectedDefect(row);
            toggleFormDialog("active");
          }}
          initialLoading={searching}
          field="Ativo"
        />
      );
    },
    sort: true,
  },
  {
    dataField: "editar",
    text: "",
    isDummyField: true,
    formatter: (cell, row) => {
      return searching ? null : (
        <Tooltip title="Editar Perfil">
          <IconButton
            aria-label="edit"
            onClick={() => {
              setSelectedDefect(row);
              toggleFormDialog("edit");
            }}
          >
            <Box
              component={Create}
              width="1.25rem!important"
              height="1.25rem!important"
            />
          </IconButton>
        </Tooltip>
      );
    },
    headerStyle: { width: 45 },
  },
  {
    dataField: "deletar",
    text: "",
    isDummyField: true,
    headerStyle: { width: 45 },
    formatter: (cell, row) =>
      searching ? null : (
        <Tooltip title="Deletar Motivo">
          <IconButton
            aria-label="delete"
            onClick={() => {
              setSelectedDefect(row);
              toggleFormDialog("delete");
            }}
          >
            <Box
              component={Delete}
              width="1.25rem!important"
              height="1.25rem!important"
            />
          </IconButton>
        </Tooltip>
      ),
  },
];

const loadingItems = [
  { IdMotivo: "0" },
  { IdMotivo: "1" },
  { IdMotivo: "2" },
  { IdMotivo: "3" },
  { IdMotivo: "4" },
  { IdMotivo: "5" },
  { IdMotivo: "6" },
  { IdMotivo: "7" },
  { IdMotivo: "8" },
  { IdMotivo: "9" },
];

const { SearchBar } = Search;

export default function MotivosDefeito() {
  const snackbar = useSnackbar();
  const [defect, setDefect] = useState(loadingItems);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [searching, setSearching] = useState(true);
  const isMobile = IsMobile();

  const { setLocalStorageActions, setLocalStorageApiProblem } = useLogs();

  const toggleFormDialog = useCallback((formType) => {
    if (formType) {
      setOpenFormDialog(formType);
    }
  }, []);

  const fetchList = async () => {
    await Api.get("list-defect", "trocasDevolucoes", "?page=0", true)
      .then(({ Success, Result }) => {
        if (Success) {
          setDefect(Result.ListaDefeitos);
        }
      })
      .catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });
        setLocalStorageApiProblem({
          idMenuAccess: "191",
          screen: "Motivos de Defeitos",
          problem: `Erro na chamada do endpoint list-defect - ${error}`,
        });
      })
      .finally(() => setSearching(false));
  };

  const handleRefresh = async () => {
    setSearching(true);

    return fetchList();
  };

  useEffect(() => {
    fetchList();
    setLocalStorageActions({
      nomeTela: "Motivos de Defeitos",
      idMenu: "57",
      acao: "Acessou o menu Motivos de Defeitos",
    });
  }, []);

  return (
    <div>
      <HeaderClientes section="Motivos de Defeito" subsection="Gerenciamento" />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <Grid container>
          <Grid item xs={12}>
            <Card>
              <ToolkitTable
                keyField="IdDefeito"
                dataTable={searching ? loadingItems : defect}
                columnModel={getTableColumns(
                  setSelectedDefect,
                  toggleFormDialog,
                  searching
                )}
                search
                allowEmpty
                CustomHeader={({ searchProps }) => (
                  <Grid
                    container
                    className="pl-4"
                    style={{ padding: isMobile ? "0 24px 10px" : "0 24px" }}
                  >
                    <Grid item xs={isMobile ? 12 : 4}>
                      <Box
                        component={Typography}
                        variant="h2"
                        marginBottom="0!important"
                      >
                        <Box component="span">Lista de Defeitos</Box>
                        <IconButtonLoader
                          size="medium"
                          extraStyle={{
                            width: 24,
                            height: 24,
                            marginLeft: "0.3rem",
                          }}
                          title="Recarregar lista"
                          onClick={handleRefresh}
                          icon={<Refresh color="primary" />}
                          aria-label="refresh"
                          color="primary"
                          disabled={searching}
                        />
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={isMobile ? 12 : 8}
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: isMobile ? "flex-start" : "flex-end",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <SearchBar
                        className="form-control"
                        placeholder="Pesquisar Defeito"
                        {...searchProps}
                        disabled={searching}
                      />
                      <Box
                        justifyContent="flex-end"
                        display="flex"
                        flexWrap="wrap"
                        style={{ marginLeft: isMobile ? 0 : 15 }}
                      >
                        <Button
                          variant="contained"
                          onClick={() => toggleFormDialog("create")}
                          disabled={searching}
                        >
                          <Box component={Add} position="relative" top="-2px" />{" "}
                          Criar
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                )}
                pagination={{
                  options: [25, 50, 100],
                  defaultOption: 25,
                }}
              />
            </Card>
          </Grid>
        </Grid>
      </Container>
      {Boolean(openFormDialog) && (
        <FormDefectReasonDialog
          open
          formType={openFormDialog}
          onClose={() => {
            setOpenFormDialog(null);
            setSelectedDefect(null);
            fetchList();
          }}
          selectedDefect={selectedDefect}
        />
      )}
    </div>
  );
}
